import { z } from 'zod';
/**
 * Status enums and closed vocabularies — single source of truth for both the
 * DB CHECK constraints (U1) and every DTO. Values are snake_case and match
 * ARCHITECTURE.md verbatim where it enumerates them.
 */
// ---------------------------------------------------------------- tenancy/auth
export const USER_ROLES = ['platform_admin', 'owner', 'financial', 'supervisor', 'foreman', 'worker'];
export const UserRoleSchema = z.enum(USER_ROLES);
/** Display names. `platform_admin` reads as "Admin" (the Job Site AI operator, not a company role). */
export const ROLE_LABELS = {
    platform_admin: 'Admin',
    owner: 'Owner',
    financial: 'Financial',
    supervisor: 'Supervisor',
    foreman: 'Foreman',
    worker: 'Worker',
};
export const USER_STATUSES = ['active', 'invited', 'disabled'];
export const UserStatusSchema = z.enum(USER_STATUSES);
export const CONTRACTOR_SUBTYPES = ['general', 'niche'];
export const ContractorSubtypeSchema = z.enum(CONTRACTOR_SUBTYPES);
// ------------------------------------------------------------------- libraries
export const BURDEN_KINDS = ['percent', 'per_hour'];
export const BurdenKindSchema = z.enum(BURDEN_KINDS);
export const WAGE_RATE_SET_KINDS = ['standard', 'prevailing'];
export const WageRateSetKindSchema = z.enum(WAGE_RATE_SET_KINDS);
export const EQUIPMENT_UNITS = ['hour', 'day', 'week', 'month'];
export const EquipmentUnitSchema = z.enum(EQUIPMENT_UNITS);
export const EQUIPMENT_OWNERSHIP = ['owned', 'rented'];
export const EquipmentOwnershipSchema = z.enum(EQUIPMENT_OWNERSHIP);
export const ASSEMBLY_COMPONENT_KINDS = ['labor', 'material', 'equipment'];
export const AssemblyComponentKindSchema = z.enum(ASSEMBLY_COMPONENT_KINDS);
export const LIBRARY_IMPORT_STATUSES = ['uploaded', 'mapped', 'committed', 'failed'];
export const LibraryImportStatusSchema = z.enum(LIBRARY_IMPORT_STATUSES);
export const LIBRARY_IMPORT_TARGETS = ['labor_roles', 'materials', 'equipment_rates'];
export const LibraryImportTargetSchema = z.enum(LIBRARY_IMPORT_TARGETS);
/** Library item types that carry revisions / can be estimate line sources. */
export const LIBRARY_ITEM_TYPES = [
    'labor_role',
    'crew',
    'material',
    'equipment_rate',
    'assembly',
    'wage_rate_set',
];
export const LibraryItemTypeSchema = z.enum(LIBRARY_ITEM_TYPES);
// ------------------------------------------------------------------- estimates
export const ESTIMATE_STATUSES = ['draft', 'sent', 'viewed', 'accepted', 'declined', 'expired'];
export const EstimateStatusSchema = z.enum(ESTIMATE_STATUSES);
export const ESTIMATE_VERSION_STATUSES = [
    'sent',
    'viewed',
    'accepted',
    'declined',
    'expired',
    'superseded',
];
export const EstimateVersionStatusSchema = z.enum(ESTIMATE_VERSION_STATUSES);
export const ESTIMATE_SECTION_KINDS = ['standard', 'alternate', 'allowance', 'sub_rollup'];
export const EstimateSectionKindSchema = z.enum(ESTIMATE_SECTION_KINDS);
export const ESTIMATE_LINE_KINDS = [
    'labor',
    'material',
    'equipment',
    'assembly',
    'subquote',
    'allowance',
    'custom',
];
export const EstimateLineKindSchema = z.enum(ESTIMATE_LINE_KINDS);
export const ESTIMATE_NOTE_KINDS = ['inclusion', 'exclusion', 'clarification', 'assumption'];
export const EstimateNoteKindSchema = z.enum(ESTIMATE_NOTE_KINDS);
/**
 * Markup vs margin must NEVER be ambiguous (product law) — every markup value
 * anywhere in the system carries one of these explicit mode labels.
 */
export const MARKUP_MODES = ['markup', 'margin'];
export const MarkupModeSchema = z.enum(MARKUP_MODES);
// ------------------------------------------------------------------- proposals
export const PROPOSAL_PRESENTATIONS = ['lump_sum', 'detailed'];
export const ProposalPresentationSchema = z.enum(PROPOSAL_PRESENTATIONS);
export const PROPOSAL_EVENT_KINDS = [
    'viewed',
    'pdf_downloaded',
    'consent_given',
    'signed_copy_viewed',
];
export const ProposalEventKindSchema = z.enum(PROPOSAL_EVENT_KINDS);
export const PROPOSAL_RESPONSE_KINDS = ['accepted', 'declined'];
export const ProposalResponseKindSchema = z.enum(PROPOSAL_RESPONSE_KINDS);
// ---------------------------------------------------------------- sharing seam
export const SCOPE_PACKAGE_STATUSES = ['draft', 'open', 'closed', 'awarded', 'cancelled'];
export const ScopePackageStatusSchema = z.enum(SCOPE_PACKAGE_STATUSES);
export const GRANT_STATUSES = ['invited', 'active', 'revoked', 'declined'];
export const GrantStatusSchema = z.enum(GRANT_STATUSES);
export const SUB_QUOTE_STATUSES = [
    'draft',
    'submitted',
    'revised',
    'accepted',
    'declined',
    'withdrawn',
    'expired',
];
export const SubQuoteStatusSchema = z.enum(SUB_QUOTE_STATUSES);
export const SUB_QUOTE_KINDS = ['lump_sum', 'line_items'];
export const SubQuoteKindSchema = z.enum(SUB_QUOTE_KINDS);
export const VETTING_REQUEST_STATUSES = ['requested', 'submitted'];
export const VettingRequestStatusSchema = z.enum(VETTING_REQUEST_STATUSES);
// -------------------------------------------------------------------- projects
/**
 * Projects — execution tracking for work already won (bible §6.2 / roadmap
 * P9.1). NOTE: `project_sites` in the CRM is a CLIENT ADDRESS record and has
 * nothing to do with these vocabularies.
 */
export const PROJECT_STATUSES = ['planned', 'active', 'on_hold', 'complete', 'cancelled'];
export const ProjectStatusSchema = z.enum(PROJECT_STATUSES);
/**
 * One assignment per counterparty on a project. `complete` is the SUB's
 * declaration that its portion is finished; `accepted` is the CONTRACTOR's
 * sign-off, after which the checklist freezes. Access ends at `removed`.
 */
export const PROJECT_ASSIGNMENT_STATUSES = [
    'assigned',
    'in_progress',
    'complete',
    'accepted',
    'removed',
];
export const ProjectAssignmentStatusSchema = z.enum(PROJECT_ASSIGNMENT_STATUSES);
export const PROJECT_WORK_ITEM_STATUSES = ['todo', 'blocked', 'done'];
export const ProjectWorkItemStatusSchema = z.enum(PROJECT_WORK_ITEM_STATUSES);
/**
 * Work-log kinds. `correction` supersedes an earlier entry by the SAME author
 * on the SAME assignment — logs are append-only, so a mistake is fixed by
 * appending, never by editing. `system` entries are written by the API.
 */
export const PROJECT_WORK_LOG_KINDS = [
    'note',
    'progress',
    'item_completed',
    'blocked',
    'portion_complete',
    'correction',
    'system',
];
export const ProjectWorkLogKindSchema = z.enum(PROJECT_WORK_LOG_KINDS);
/** Kinds a human may author directly; the rest are derived by the API. */
export const PROJECT_WORK_LOG_AUTHORABLE_KINDS = ['note', 'progress', 'blocked'];
export const ProjectWorkLogAuthorableKindSchema = z.enum(PROJECT_WORK_LOG_AUTHORABLE_KINDS);
// ------------------------------------------------------------------- materials
/**
 * A materials list is the parts needed for one job — or, when it is bound to a
 * project assignment, for one subcontractor's portion of that job.
 *
 * `priced` means vendor quotes have been attached, NOT that anything was
 * bought. `archived` lists stay readable: a list is a record of what a job
 * needed, so nothing here is ever deleted to tidy up.
 */
export const MATERIAL_LIST_STATUSES = ['draft', 'priced', 'ordered', 'archived'];
export const MaterialListStatusSchema = z.enum(MATERIAL_LIST_STATUSES);
/**
 * How a quoted price was obtained. `library` is the contractor's own recorded
 * cost and needs no API key or network call — it is the day-one source.
 * `vendor_api` came from an external adapter; `manual` was typed by a human.
 *
 * The vendor's IDENTITY is a row in `material_vendors`, not a value here, so
 * adding a vendor is data, not a schema change.
 */
export const MATERIAL_QUOTE_SOURCES = ['library', 'vendor_api', 'manual'];
export const MaterialQuoteSourceSchema = z.enum(MATERIAL_QUOTE_SOURCES);
// ------------------------------------------------------------------ compliance
export const COMPLIANCE_RULE_TYPES = [
    'license_display',
    'disclosure_block',
    'cancellation_notice',
    'prelien_notice',
    'deadline_parameter',
    'checklist_item',
    'awareness_flag',
];
export const ComplianceRuleTypeSchema = z.enum(COMPLIANCE_RULE_TYPES);
export const WORK_TYPES = ['residential', 'commercial', 'public', 'any'];
export const WorkTypeSchema = z.enum(WORK_TYPES);
/**
 * Verification status of a compliance pack. The product NEVER asserts
 * compliance; these labels are rendered verbatim on every surface that shows
 * pack content, alongside each rule's `boundary_note`.
 */
export const COMPLIANCE_VERIFICATIONS = ['verified', 'unverified_draft', 'federal_anchor'];
export const ComplianceVerificationSchema = z.enum(COMPLIANCE_VERIFICATIONS);
export const COMPLIANCE_PACK_STATUSES = ['draft', 'active', 'superseded'];
export const CompliancePackStatusSchema = z.enum(COMPLIANCE_PACK_STATUSES);
// -------------------------------------------------------------------- backbone
export const FILE_PURPOSES = [
    'logo',
    'cover_image',
    'coi',
    'attachment',
    'proposal_pdf',
    'signed_proposal_pdf',
    'import_csv',
    'export_zip',
    /** Field photo on a project / work log (0021_project_attachments). */
    'work_photo',
];
export const FilePurposeSchema = z.enum(FILE_PURPOSES);
export const EXPORT_JOB_STATUSES = ['queued', 'running', 'complete', 'failed'];
export const ExportJobStatusSchema = z.enum(EXPORT_JOB_STATUSES);
export const FOLLOW_UP_ENTITY_TYPES = [
    'client',
    'estimate',
    'proposal',
    'scope_package',
    'sub_quote',
];
export const FollowUpEntityTypeSchema = z.enum(FOLLOW_UP_ENTITY_TYPES);
export const FOLLOW_UP_STATUSES = ['open', 'done', 'dismissed'];
export const FollowUpStatusSchema = z.enum(FOLLOW_UP_STATUSES);
/**
 * Audit actions are namespaced `entity.event` strings. The set is open
 * (modules add focus actions), but the shape is enforced and well-known
 * actions are enumerated for tooling/filters.
 */
export const AUDIT_ACTION_REGEX = /^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/;
export const AuditActionSchema = z.string().regex(AUDIT_ACTION_REGEX);
export const KNOWN_AUDIT_ACTIONS = [
    'auth.signup',
    'auth.login',
    'auth.login_failed',
    'auth.totp_activated',
    'auth.session_revoked',
    'company.updated',
    'company.user_created',
    'company.user_updated',
    'library.import_committed',
    'estimate.created',
    'estimate.sent',
    'estimate.revised',
    'estimate.price_changed_post_send',
    'estimate.duplicated',
    'proposal.viewed',
    'proposal.pdf_downloaded',
    'proposal.consent_given',
    'proposal.signed',
    'proposal.declined',
    'seam.grant_created',
    'seam.grant_revoked',
    'seam.invite_claimed',
    'seam.package_published',
    'seam.quote_submitted',
    'seam.quote_withdrawn',
    'seam.quote_accepted',
    'seam.quote_declined',
    'seam.vetting_requested',
    'seam.vetting_submitted',
    'project.created',
    'project.updated',
    'project.status_changed',
    'project.sub_added',
    'project.sub_removed',
    'project.work_item_added',
    'project.work_item_removed',
    'project.portion_completed',
    'project.portion_accepted',
    'project.portion_reopened',
    'worklog.entry_added',
    'worklog.entry_corrected',
    'worklog.item_completed',
    'worklog.item_reopened',
    'material.list_created',
    'material.list_updated',
    'material.list_archived',
    'material.item_added',
    'material.item_removed',
    'material.list_priced',
    'material.list_imported_to_estimate',
    'file.uploaded',
    'export.created',
    // Roles & users (2026-09-17)
    'platform.act_as_started',
    'platform.act_as_stopped',
    'user.role_changed',
    'user.labor_role_set',
    'project.member_added',
    'project.member_removed',
    'crew.created',
    'crew.updated',
    'crew.members_set',
    'invite.created',
    'invite.rotated',
    'invite.revoked',
    'invite.sent',
    'invite.claimed',
    'time.clocked_in',
    'time.clocked_out',
    'time.entry_added',
    'time.entry_corrected',
    'attendance.marked',
    'project.cost_logged',
];
//# sourceMappingURL=enums.js.map